/**
 * Hive Flows — visual workflow editor route.
 *
 * The v2 ``SurfaceLayout`` shell + the node palette (left) and the @xyflow
 * canvas (center). Mode-tier toggle (Essenziale/Comune/Avanzato) drives the
 * palette's progressive disclosure. Save persists the graph; Prova runs a
 * dry-run (zero side-effects) — test-before-activate.
 */

import {
  type FlowGraph,
  type FlowRun,
  type FlowScheduleType,
  type ScheduleInterpretation,
  getFlow,
  runFlow,
  updateFlow,
} from "@ando/core";
import { SegmentedControl, SurfaceLayout, cn, toast } from "@ando/ui";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  FlaskConical,
  LayoutDashboard,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  Pencil,
  Play,
  Save,
  TriangleAlert,
  Workflow,
  X,
} from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { useActiveContextScope } from "@/features/active-context";
import { AiCopilotBar } from "@/features/hive-flows/components/AiCopilotBar";
import { DryRunPanel } from "@/features/hive-flows/components/DryRunPanel";
import { FlowActivateControl } from "@/features/hive-flows/components/FlowActivateControl";
import { FlowCanvas } from "@/features/hive-flows/components/FlowCanvas";
import { FlowFormPublish } from "@/features/hive-flows/components/FlowFormPublish";
import { FlowShareControl } from "@/features/hive-flows/components/FlowShareControl";
import { FlowVersionMenu } from "@/features/hive-flows/components/FlowVersionMenu";
import { NodeInspector } from "@/features/hive-flows/components/NodeInspector";
import { NodePalette } from "@/features/hive-flows/components/NodePalette";
import { TriggerTestDataDialog } from "@/features/hive-flows/components/TriggerTestDataDialog";
import { ExperienceBuilderPanel } from "@/features/hive-flows/experience/ExperienceBuilderPanel";
import { flowsQueryKeys } from "@/features/hive-flows/hooks/useFlows";
import { usePalette } from "@/features/hive-flows/hooks/usePalette";
import { FlowRunWorkspace } from "@/features/hive-flows/run/FlowRunWorkspace";
import { type ModeTier, useFlowEditorStore } from "@/features/hive-flows/state/flowEditorStore";
import { validateWorkspaceSearch } from "@/features/workspace-router";

export const Route = createFileRoute("/_authed/workspace/flows/$flowId")({
  validateSearch: validateWorkspaceSearch,
  component: FlowEditorRoute,
});

const TIERS: { id: ModeTier; label: MessageDescriptor }[] = [
  { id: "essential", label: msg`Essenziale` },
  { id: "common", label: msg`Comune` },
  { id: "advanced", label: msg`Avanzato` },
];

function EditorFocusPortal({
  active,
  children,
}: {
  active: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  // The workspace shell uses transforms for its resizable split panes. A
  // `position: fixed` descendant would therefore remain clipped to that pane
  // instead of the viewport. Portalling the *same* editor tree to <body> is the
  // browser-correct full-screen primitive and keeps graph + UI designer DRY.
  if (active && typeof document !== "undefined") {
    return createPortal(children, document.body);
  }
  return <>{children}</>;
}

function FlowEditorRoute(): React.JSX.Element {
  const { i18n } = useLingui();
  const { flowId } = useParams({ from: "/_authed/workspace/flows/$flowId" });
  const navigate = useNavigate({ from: "/workspace/flows/$flowId" });
  const search = useSearch({ from: "/_authed/workspace/flows/$flowId" }) as {
    review?: string;
    mode?: string;
    run?: string;
    editor?: string;
  };
  const { items, byType, isLoading } = usePalette();
  const queryClient = useQueryClient();

  const modeTier = useFlowEditorStore((s) => s.modeTier);
  const setModeTier = useFlowEditorStore((s) => s.setModeTier);
  const addNode = useFlowEditorStore((s) => s.addNode);
  const toGraph = useFlowEditorStore((s) => s.toGraph);
  const markSaved = useFlowEditorStore((s) => s.markSaved);
  const dirty = useFlowEditorStore((s) => s.dirty);
  const loadGraph = useFlowEditorStore((s) => s.loadGraph);
  const hydrateVisuals = useFlowEditorStore((s) => s.hydrateVisuals);
  const nodes = useFlowEditorStore((s) => s.nodes);
  const edges = useFlowEditorStore((s) => s.edges);
  const selectNode = useFlowEditorStore((s) => s.selectNode);
  const graphSurface = useFlowEditorStore((s) => s.graphMeta.surface);
  const setSurface = useFlowEditorStore((s) => s.setSurface);
  const experienceGraph = React.useMemo<FlowGraph>(
    () => ({
      surface: graphSurface,
      nodes: nodes.map((node) => ({
        id: node.id,
        node_type: node.data.nodeType,
        config: node.data.config,
        position: { x: Math.round(node.position.x), y: Math.round(node.position.y) },
      })),
      edges: edges.map((edge) => ({
        source: edge.source,
        target: edge.target,
        condition: (edge.data as { condition?: string } | undefined)?.condition ?? null,
      })),
    }),
    [edges, graphSurface, nodes],
  );

  // Reconciliation banner (issue #936): the flows hub passes the flagged
  // node ids via ``?review=`` right after forking a team-shared flow, a
  // fresh, independently-owned draft whose connector-touching nodes
  // (email, Drive, database, …) still point at the ORIGINAL owner's
  // account. Transient by design (no persisted "reviewed" flag): reload or
  // a direct link simply shows no banner, matching the agreed non-blocking
  // UX (the flow stays a draft either way until manually activated).
  const reviewNodeIds = React.useMemo(
    () => new Set((search.review ?? "").split(",").filter(Boolean)),
    [search.review],
  );
  const [bannerDismissed, setBannerDismissed] = React.useState(false);
  const [isEditorFocused, setIsEditorFocused] = React.useState(false);
  const [studioMoreOpen, setStudioMoreOpen] = React.useState(false);
  React.useEffect(() => {
    if (!isEditorFocused) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsEditorFocused(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEditorFocused]);
  const reviewNodes = React.useMemo(
    () => nodes.filter((n) => reviewNodeIds.has(n.id)),
    [nodes, reviewNodeIds],
  );
  const dismissReviewBanner = React.useCallback(() => {
    setBannerDismissed(true);
    void navigate({ search: (prev) => ({ ...prev, review: undefined }), replace: true });
  }, [navigate]);

  // Load the saved flow's graph once (then re-hydrate node visuals from the
  // palette, since the catalog never rides the wire). Reset on unmount.
  const { data: flow } = useQuery({
    queryKey: flowsQueryKeys.detail(flowId),
    queryFn: () => getFlow(flowId),
  });

  // Mode: "Utilizza" (run + reopen history) vs "Modifica" (this editor).
  // Default to Utilizza once the flow is built — the operator lives there; a
  // fresh/empty flow opens in Modifica to be constructed. URL-driven so a
  // refresh / deep-link keeps the mode and the reopened run (``?run``).
  const graphBuilt = (flow?.graph?.nodes?.length ?? 0) > 0;
  const mode: "utilizza" | "modifica" =
    search.mode === "utilizza" || search.mode === "modifica"
      ? search.mode
      : graphBuilt
        ? "utilizza"
        : "modifica";
  React.useEffect(() => {
    // Resolve the implicit default exactly once. Without pinning it in the
    // URL, saving the first nodes changes `graphBuilt` and unexpectedly kicks
    // the user from the editor into the run surface mid-session.
    if (!flow || search.mode === "utilizza" || search.mode === "modifica") return;
    void navigate({
      search: (prev) => ({ ...prev, mode: graphBuilt ? "utilizza" : "modifica" }),
      replace: true,
    });
  }, [flow, graphBuilt, navigate, search.mode]);
  const focusRunId = search.run;
  const setMode = React.useCallback(
    (m: "utilizza" | "modifica") => void navigate({ search: (prev) => ({ ...prev, mode: m }) }),
    [navigate],
  );
  const focusRun = React.useCallback(
    (runId: string) => void navigate({ search: (prev) => ({ ...prev, run: runId }) }),
    [navigate],
  );
  const clearFocusRun = React.useCallback(
    () => void navigate({ search: (prev) => ({ ...prev, run: undefined }) }),
    [navigate],
  );
  const editorView: "flow" | "experience" = search.editor === "ui" ? "experience" : "flow";
  const setEditorView = React.useCallback(
    (view: "flow" | "experience") =>
      void navigate({
        search: (prev) => ({ ...prev, editor: view === "experience" ? "ui" : undefined }),
      }),
    [navigate],
  );

  // Active context — the agent grounds "explain this flow", "what runs
  // on Monday", "duplicate it with a new schedule" against THIS flow.
  useActiveContextScope(
    flow
      ? {
          kind: "flow",
          id: flow.flow_id,
          title: flow.name,
          summary:
            [flow.schedule_type, flow.is_active ? i18n._(msg`attivo`) : i18n._(msg`inattivo`)]
              .filter(Boolean)
              .join(" · ") || undefined,
          meta: {
            schedule_type: flow.schedule_type,
            is_active: flow.is_active,
            status: flow.status,
            node_count: flow.graph?.nodes?.length ?? 0,
            ...(flow.description ? { description: flow.description } : {}),
          },
        }
      : null,
  );

  // `loaded` gates Save/Prova/Esegui: toGraph() reads the Zustand store, which
  // starts empty and is only populated once loadGraph() below has actually run.
  // Clicking one of those buttons before that point serializes the empty
  // initial state and overwrites the flow's real saved graph.
  const [loaded, setLoaded] = React.useState(false);
  const loadedRef = React.useRef(false);
  React.useEffect(() => {
    if (!loadedRef.current && flow && byType.size > 0) {
      loadGraph(flow.graph);
      hydrateVisuals(byType);
      loadedRef.current = true;
      setLoaded(true);
    }
  }, [flow, byType, loadGraph, hydrateVisuals]);
  React.useEffect(
    () => () => {
      // React StrictMode intentionally mounts, cleans up and mounts effects a
      // second time in development. Reset the local guard together with the
      // store, otherwise the second pass sees ``loadedRef=true`` and leaves a
      // persisted non-empty graph rendered as an empty canvas.
      loadedRef.current = false;
      useFlowEditorStore.getState().reset();
    },
    [],
  );

  const [busy, setBusy] = React.useState<null | "save" | "test" | "run">(null);
  const [lastRun, setLastRun] = React.useState<FlowRun | null>(null);
  const [pendingMode, setPendingMode] = React.useState<"test" | "run" | null>(null);
  const [pendingSchedule, setPendingSchedule] = React.useState<ScheduleInterpretation | null>(null);

  const triggerNode = nodes.find((n) => n.data.category === "trigger");
  const triggerPorts = triggerNode ? (byType.get(triggerNode.data.nodeType)?.outputs ?? []) : [];

  const currentDraftPayload = React.useCallback(
    () => ({
      graph: toGraph() as FlowGraph,
      ...(pendingSchedule?.ok && pendingSchedule.schedule_type
        ? {
            schedule_type: pendingSchedule.schedule_type as FlowScheduleType,
            cron_expression: pendingSchedule.cron_expression ?? null,
            interval_minutes: pendingSchedule.interval_minutes ?? null,
            run_at: pendingSchedule.run_at ?? null,
            timezone: pendingSchedule.timezone ?? undefined,
          }
        : {}),
    }),
    [pendingSchedule, toGraph],
  );

  const handleAddClick = React.useCallback(
    (item: Parameters<typeof addNode>[0]) => {
      const count = useFlowEditorStore.getState().nodes.length;
      addNode(item, { x: 80, y: count * 120 });
    },
    [addNode],
  );

  const handleSave = React.useCallback(async () => {
    setBusy("save");
    try {
      const updated = await updateFlow(flowId, currentDraftPayload());
      // #1024: this call bypasses useUpdateFlow()'s mutation (no built-in
      // cache sync), so the route's own `flow` query stayed on the
      // PRE-save graph until an unrelated refetch happened to occur. That
      // was invisible before, but FlowActivateControl now reads
      // flow.graph.nodes to decide which schedule types to offer, so a
      // trigger.schedule node added and saved could still show "Pubblica"
      // as disabled until a manual page reload. Sync the cache with the
      // server's response immediately, same as useUpdateFlow does.
      queryClient.setQueryData(flowsQueryKeys.detail(flowId), updated);
      markSaved();
      setPendingSchedule(null);
      toast.success(i18n._(msg`Flow salvato`));
    } catch {
      toast.error(i18n._(msg`Salvataggio non riuscito`));
    } finally {
      setBusy(null);
    }
  }, [currentDraftPayload, flowId, markSaved, i18n, queryClient]);

  // Shared runner (PR #27): non-manual triggers open the trigger-data dialog
  // first so ``trigger_data`` is never sent empty. Toast strings stay Lingui
  // (frd) — hardcoded IT strings would violate the ando-client i18n rule.
  const runFlowWithData = React.useCallback(
    async (mode: "test" | "run", triggerData: Record<string, string>) => {
      setBusy(mode);
      try {
        const updated = await updateFlow(flowId, currentDraftPayload());
        queryClient.setQueryData(flowsQueryKeys.detail(flowId), updated);
        setPendingSchedule(null);
        const run = await runFlow(flowId, { dry_run: mode === "test", trigger_data: triggerData });
        setLastRun(run);
        if (mode === "run") {
          if (run.status === "success") toast.success(i18n._(msg`Flow eseguito`));
          else if (run.status === "pending_approval")
            toast.info(i18n._(msg`In attesa di approvazione`));
          else if (run.status === "waiting") toast.info(i18n._(msg`In attesa (nodo «Attendi»)`));
          else toast.error(run.error_message ?? i18n._(msg`Esecuzione fallita`));
        }
      } catch {
        toast.error(
          mode === "test"
            ? i18n._(msg`Anteprima non riuscita`)
            : i18n._(msg`Esecuzione non riuscita`),
        );
      } finally {
        setBusy(null);
      }
    },
    [currentDraftPayload, flowId, i18n, queryClient],
  );

  const handleTest = React.useCallback(() => {
    if (triggerPorts.length > 0) setPendingMode("test");
    else void runFlowWithData("test", {});
  }, [triggerPorts, runFlowWithData]);

  // Real manual run (executes actions for real) — for "Manuale" flows.
  const handleRun = React.useCallback(() => {
    if (triggerPorts.length > 0) setPendingMode("run");
    else void runFlowWithData("run", {});
  }, [triggerPorts, runFlowWithData]);

  const handleConfirmTriggerData = React.useCallback(
    (data: Record<string, string>) => {
      const mode = pendingMode;
      setPendingMode(null);
      if (mode) void runFlowWithData(mode, data);
    },
    [pendingMode, runFlowWithData],
  );

  return (
    <SurfaceLayout contentClassName="overflow-hidden p-0">
      {mode === "utilizza" ? (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-cream">
          <div className="flex h-12 shrink-0 items-center gap-2 border-black/10 border-b bg-[#fcfbf8] px-3">
            <Link
              to="/workspace/flows"
              aria-label={i18n._(msg`Torna ai tuoi flow`)}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-ink-3 hover:bg-black/[0.04] hover:text-ink-1"
            >
              <ArrowLeft size={14} aria-hidden />
            </Link>
            <p className="min-w-0 flex-1 truncate font-semibold text-[12px] text-ink-1">
              {flow?.name ?? i18n._(msg`Editor del flow`)}
            </p>
            <SegmentedControl<"utilizza" | "modifica">
              options={[
                {
                  value: "utilizza",
                  label: i18n._(msg`Utilizza`),
                  icon: <Play size={12} aria-hidden />,
                },
                {
                  value: "modifica",
                  label: i18n._(msg`Modifica`),
                  icon: <Pencil size={12} aria-hidden />,
                },
              ]}
              value={mode}
              onChange={setMode}
              ariaLabel={i18n._(msg`Modalità del flow`)}
            />
            <FlowActivateControl flow={flow} flowId={flowId} />
          </div>
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <FlowRunWorkspace
              flowId={flowId}
              flowName={flow?.name}
              flowGraph={flow?.graph}
              focusRunId={focusRunId}
              onFocusRun={focusRun}
              onClearFocus={clearFocusRun}
            />
          </div>
        </div>
      ) : (
        <>
          {reviewNodes.length > 0 && !bannerDismissed ? (
            <div className="mb-3 flex flex-col gap-2 rounded-[12px] border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-start gap-2">
                <TriangleAlert size={15} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[12.5px] text-ink-1">
                    {reviewNodes.length === 1
                      ? "1 nodo usa ancora un account collegato di chi ha condiviso questo flow"
                      : `${reviewNodes.length} nodi usano ancora un account collegato di chi ha condiviso questo flow`}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-3">
                    Verificali prima di attivare il flow: clicca un nodo per aprirlo.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {reviewNodes.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => selectNode(n.id)}
                        className="rounded-full border border-amber-500/40 bg-white px-2.5 py-1 font-medium text-[11.5px] text-ink-1 transition-colors hover:bg-amber-500/10"
                      >
                        {n.data.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={dismissReviewBanner}
                  aria-label="Chiudi l'avviso"
                  className="shrink-0 rounded-md p-1 text-ink-3 transition-colors hover:bg-ink-1/[0.06] hover:text-ink-1"
                >
                  <X size={14} aria-hidden />
                </button>
              </div>
            </div>
          ) : null}
          <EditorFocusPortal active={isEditorFocused}>
            <div
              className={cn(
                "flex h-full min-h-0 flex-col overflow-hidden",
                isEditorFocused &&
                  "fixed inset-2 z-[90] rounded-xl border border-black/10 bg-[#f4f5f2] shadow-[0_24px_100px_rgba(0,0,0,0.28)]",
                editorView === "experience"
                  ? "bg-transparent"
                  : "rounded-[14px] border border-hairline bg-white/40",
              )}
            >
              <div className="relative flex h-12 shrink-0 items-center gap-2 border-black/10 border-b bg-[#fcfbf8] px-3">
                <button
                  type="button"
                  onClick={() => setMode("utilizza")}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2 font-medium text-[11px] text-ink-3 hover:bg-black/[0.04] hover:text-ink-1"
                >
                  <ArrowLeft size={13} aria-hidden /> Utilizza
                </button>
                <div className="min-w-0 flex-1 border-black/10 border-l pl-3">
                  <p className="truncate font-semibold text-[12px] text-ink-1">
                    {flow?.name ?? "Editor del flow"}
                  </p>
                </div>
                <SegmentedControl<"flow" | "experience">
                  options={[
                    { value: "flow", label: "Nodi", icon: <Workflow size={12} aria-hidden /> },
                    {
                      value: "experience",
                      label: "Interfaccia",
                      icon: <LayoutDashboard size={12} aria-hidden />,
                    },
                  ]}
                  value={editorView}
                  onChange={setEditorView}
                  ariaLabel="Area di progettazione"
                />
                {editorView === "flow" ? (
                  <SegmentedControl
                    options={TIERS.map((tier) => ({
                      value: tier.id,
                      label: i18n._(tier.label),
                    }))}
                    value={modeTier}
                    onChange={setModeTier}
                    ariaLabel="Complessità dei nodi"
                  />
                ) : null}
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={busy !== null || !loaded}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 font-medium text-[11px] text-ink-2 hover:bg-cream disabled:opacity-50"
                >
                  <FlaskConical size={13} aria-hidden /> Prova
                </button>
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={busy !== null || !loaded}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-white px-2.5 font-medium text-[11px] text-ink-2 hover:bg-cream disabled:opacity-50"
                >
                  <Play size={13} aria-hidden /> {busy === "run" ? "Eseguo…" : "Esegui"}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy !== null || !loaded}
                  className={cn(
                    "inline-flex h-8 items-center gap-1.5 rounded-md px-3 font-medium text-[11px] transition-colors",
                    dirty
                      ? "bg-ink-1 text-white hover:bg-black"
                      : "border border-hairline bg-white text-ink-2",
                  )}
                >
                  <Save size={13} aria-hidden />
                  {dirty ? "Salva modifiche" : "Salvato"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditorFocused((focused) => !focused)}
                  aria-label={
                    isEditorFocused ? "Esci dalla modalità focus" : "Apri a schermo intero"
                  }
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-hairline bg-white px-3 font-medium text-[11px] text-ink-2 hover:bg-cream"
                >
                  {isEditorFocused ? (
                    <Minimize2 size={13} aria-hidden />
                  ) : (
                    <Maximize2 size={13} aria-hidden />
                  )}
                  {isEditorFocused ? "Riduci" : "Focus"}
                </button>
                <button
                  type="button"
                  aria-label="Altre impostazioni del flow"
                  aria-expanded={studioMoreOpen}
                  onClick={() => setStudioMoreOpen((open) => !open)}
                  className="grid h-8 w-8 place-items-center rounded-md border border-hairline bg-white text-ink-2 hover:bg-cream"
                >
                  <MoreHorizontal size={15} aria-hidden />
                </button>
                {studioMoreOpen ? (
                  <div className="absolute top-11 right-3 z-[80] flex items-center gap-2 rounded-xl border border-black/10 bg-white p-2 shadow-[0_16px_48px_rgba(20,24,30,0.18)]">
                    <FlowVersionMenu
                      flowId={flowId}
                      onRestored={(restored) => {
                        loadGraph(restored.graph);
                        hydrateVisuals(byType);
                        setStudioMoreOpen(false);
                      }}
                    />
                    <FlowFormPublish flowId={flowId} />
                    <FlowShareControl flow={flow} flowId={flowId} />
                    <FlowActivateControl flow={flow} flowId={flowId} />
                  </div>
                ) : null}
              </div>
              <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
                {isLoading || !loaded ? (
                  <div className="flex h-full w-full items-center justify-center text-[13px] text-ink-3">
                    {i18n._(msg`Caricamento palette…`)}
                  </div>
                ) : editorView === "experience" ? (
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <ExperienceBuilderPanel
                      flowName={flow?.name}
                      flowRevision={flow?.version}
                      nodes={nodes}
                      graph={experienceGraph}
                      onGraphChange={(nextGraph) => {
                        loadGraph(nextGraph);
                        hydrateVisuals(byType);
                      }}
                      onScheduleChange={setPendingSchedule}
                      byType={byType}
                      previewRun={
                        lastRun
                          ? {
                              ...lastRun,
                              trigger_data: {},
                              context: Object.fromEntries(
                                lastRun.node_runs.map((nodeRun) => [
                                  nodeRun.node_id,
                                  nodeRun.output,
                                ]),
                              ),
                            }
                          : undefined
                      }
                      surface={graphSurface}
                      onSurfaceChange={setSurface}
                      onPublish={(surface) => {
                        setSurface(surface);
                        void handleSave();
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <NodePalette items={items} modeTier={modeTier} onAdd={handleAddClick} />
                    <div className="relative min-w-0 flex-1">
                      <FlowCanvas byType={byType} />
                      <AiCopilotBar
                        flowId={flowId}
                        flowName={flow?.name}
                        byType={byType}
                        surface={graphSurface}
                        onSurfaceChange={setSurface}
                        onScheduleChange={setPendingSchedule}
                      />
                      <DryRunPanel run={lastRun} onClose={() => setLastRun(null)} />
                      <TriggerTestDataDialog
                        open={pendingMode !== null}
                        onOpenChange={(o) => !o && setPendingMode(null)}
                        ports={triggerPorts}
                        mode={pendingMode ?? "test"}
                        busy={busy !== null}
                        onConfirm={handleConfirmTriggerData}
                      />
                    </div>
                    <NodeInspector flowId={flowId} byType={byType} modeTier={modeTier} />
                  </>
                )}
              </div>
            </div>
          </EditorFocusPortal>
        </>
      )}
    </SurfaceLayout>
  );
}
